import React, { ReactElement, useCallback, useState } from 'react';

import Tooltip, { TooltipProps } from '@/components/mui/Tooltip/index';

// A `Tooltip` that only shows when the wrapped child's text is actually clipped
// (ellipsis active). Truncation is measured on the child element on `mouseEnter`
// via `scrollWidth > clientWidth`, so it stays correct even when the child's
// available width changes between renders (e.g. a list row that reserves space for
// hover-revealed action buttons).
//
// The tooltip's `open` state is controlled directly from the child's enter/leave
// handlers, which makes the "show only when truncated" decision deterministic:
// gating via `disabled`/`disableHoverListener` fails on the first hover because MUI
// evaluates the title before our measurement state has committed and then will not
// re-open on a mere prop change. By owning `open` we show the tooltip precisely when
// we set it. MUI's own listeners stay enabled and `onClose` is wired up, so the
// tooltip still closes on blur / Escape / scroll even if a `mouseleave` is missed.
//
// The child must forward its ref + spread props to a DOM node (any MUI component or
// a `forwardRef` host element) — same contract as MUI `Tooltip`.
//
// `measure` picks the element to measure when it is not the child (e.g. an `<input>`).
export type TruncatedTooltipProps = Omit<TooltipProps, 'open' | 'onOpen' | 'onClose'> & {
  measure?: (el: HTMLElement) => Element | null;
};

const TruncatedTooltip = ({ children, measure, ...rest }: TruncatedTooltipProps): ReactElement => {
  const [open, setOpen] = useState(false);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const el = measure ? measure(event.currentTarget) : event.currentTarget;
      setOpen(Boolean(el && el.scrollWidth > el.clientWidth));
      children.props.onMouseEnter?.(event);
    },
    [children, measure],
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setOpen(false);
      children.props.onMouseLeave?.(event);
    },
    [children],
  );

  const child = React.cloneElement(children, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  });

  // This tooltip is used inside the chat drawer (theme.zIndex.chatDrawer); the default
  // tooltip popper (theme.zIndex.tooltip) would render behind it and be invisible. Lift
  // the popper to the chat's popup layer. Scoped to this component only,
  // so no other tooltips in the app are affected.
  const { slotProps, ...restProps } = rest;
  const popper = typeof slotProps?.popper === 'function'
    ? slotProps.popper
    : { ...slotProps?.popper, sx: { zIndex: 'chatPopup', ...(slotProps?.popper?.sx as object) } };

  return (
    <Tooltip
      {...restProps}
      open={open}
      onClose={() => setOpen(false)}
      slotProps={{ ...slotProps, popper }}
    >
      {child}
    </Tooltip>
  );
};

export default TruncatedTooltip;
